import { activeFilterCount, appliedFilters } from './list-state.js';
import type { ListState } from './types.js';

/**
 * What the kit tells whoever is listening about how its forms and lists are
 * used. The kit knows nothing of statistics: the app registers a sink with
 * `setKitTelemetry` and decides what to do with these (M17, section E).
 *
 * An event says which form (the resource's key) or which area and what was
 * done — never a value. A field is named by its schema key, a search by the
 * fact that one was made.
 */
export type KitTelemetryEvent =
  | { type: 'form-opened'; form: string }
  | { type: 'form-invalid'; form: string; field: string }
  | { type: 'form-submitted'; form: string }
  | {
      type: 'form-abandoned';
      form: string;
      reason: 'left' | 'cancelled';
      /** How many fields the person had changed. */
      touched: number;
      seconds: number;
    }
  | { type: 'list-action'; area: string; action: ListAction };

export type ListAction = 'search' | 'filter' | 'sort';

type Sink = (event: KitTelemetryEvent) => void;

let sink: Sink | undefined;

/** Registers the one listener, or removes it with `undefined`. */
export function setKitTelemetry(next: Sink | undefined): void {
  sink = next;
}

/** Never throws: a listener that fails is not the form's problem. */
export function emitKitTelemetry(event: KitTelemetryEvent): void {
  try {
    sink?.(event);
  } catch {
    // Telemetry never gets in the way of the screen.
  }
}

/** One broken form is one fact, not thirty. */
const MAX_FIELDS = 10;

/**
 * The names of the fields a refused submit objects to, as dotted paths
 * (`address.street`, `channels.1.value`), from react-hook-form's error tree.
 */
export function errorFieldNames(errors: unknown): string[] {
  const names: string[] = [];
  const walk = (node: unknown, path: string): void => {
    if (names.length >= MAX_FIELDS || node === null || typeof node !== 'object') return;
    // A leaf is an error object: it has a `type` that is a string.
    if (path && typeof (node as { type?: unknown }).type === 'string') {
      names.push(path);
      return;
    }
    for (const [key, child] of Object.entries(node)) {
      if (child === undefined || key === 'ref') continue;
      walk(child, path ? `${path}.${key}` : key);
    }
  };
  walk(errors, '');
  return names.slice(0, MAX_FIELDS);
}

/**
 * What a change to a list's state amounts to, for the statistics: a new search,
 * a filter, a sort — and nothing for paging, for clearing the search or the
 * filters, or for anything else. Compared against the state it came from, since
 * some controls hand over a whole state and not a patch. Only which part moved
 * is read, never its content.
 */
export function listActionOf(before: ListState, after: ListState): ListAction | undefined {
  if (after.q && after.q !== before.q) return 'search';
  const applied = (state: ListState): string => JSON.stringify(appliedFilters(state));
  if (applied(after) !== applied(before) && activeFilterCount(after) > 0) return 'filter';
  if (after.sort !== before.sort || after.order !== before.order) {
    return after.sort ? 'sort' : undefined;
  }
  return undefined;
}
